import { firstValueFrom, fromEvent, race } from 'rxjs';
import { events } from './events';
import { kbitToBit } from './framework/utils/kbit-to-bit.fn';
import { mbitToBit } from './framework/utils/mbit-to-bit.fn';

export class Recorder {
  private stream!: MediaStream | null;
  private recorder: MediaRecorder | null;
  private data: Blob[] = [];

  async selectSource() {
    try {
      this.stream = await navigator.mediaDevices.getDisplayMedia({
        audio: true,
        video: {
          cursor: 'always',
        } as MediaTrackConstraints & { cursor: string },
      });
    } catch (e) {
      if (e instanceof DOMException && e.name === 'NotAllowedError') {
        // TODO: Should probably show message to user...
        events.emit('user-denied-access');
        return;
      } else {
        console.error(e);
      }

      events.emit('recording-cancelled');
      return;
    }

    if (this.stream === null || !this.stream.getVideoTracks()[0]) {
      events.emit('recording-cancelled');
      return;
    }

    try {
      const mimeType = this.getMimeType();

      if (mimeType === null) {
        throw new Error('no formats supported');
      }

      const options: MediaRecorderOptions = {
        audioBitsPerSecond: kbitToBit(192),
        videoBitsPerSecond: mbitToBit(20),
        mimeType,
      };

      this.recorder = new MediaRecorder(this.stream, options);

      events.emit('source-selected', { source: this.stream });

      firstValueFrom(
        race(
          fromEvent(this.stream, 'inactive'),
          fromEvent(this.stream.getVideoTracks()[0], 'ended')
        )
      ).then(() => events.emit('stop-recording'));
    } catch (e) {
      console.error(e);
      this.stopRecording();
      events.emit('recording-cancelled');
    }
  }

  startRecording() {
    this.recorder?.addEventListener('dataavailable', (chunks) =>
      this.data.push(chunks.data)
    );
    this.recorder?.addEventListener('stop', () => {
      const recording = new Blob(this.data, { type: this.data[0].type });

      this.stream?.getVideoTracks()[0].stop();

      events.emit('recording-finished', {
        recording,
        type: MediaRecorder.isTypeSupported('video/webm') ? 'webm' : 'mp4',
      });
    });

    this.recorder?.start();
  }

  stopRecording() {
    this.stream?.getVideoTracks()[0]?.stop();
    this.recorder?.stop();
    this.reset();
  }

  reset() {
    this.recorder = null;
    this.stream = null;
    this.data = [];
  }

  private getMimeType() {
    return MediaRecorder.isTypeSupported('video/webm;codecs=vp9')
      ? 'video/webm;codecs=vp9'
      : MediaRecorder.isTypeSupported('video/webm;codecs=vp8')
      ? 'video/webm;codecs=vp8'
      : MediaRecorder.isTypeSupported('video/webm')
      ? 'video/webm'
      : MediaRecorder.isTypeSupported('video/mp4')
      ? 'video/mp4'
      : null;
  }
}
